import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';
import { hide, hideAll } from '@shared/easyfy.utils';
import { PostForm } from '@webServices/form.service';
import easyfyToaster, { MessageSeverity } from '@shared/easyfy.toaster';

// Only allow same-origin redirects to avoid open-redirect / phishing navigation.
function toSameOriginPath(redirect: unknown): string | null {
  if (typeof redirect !== 'string' || redirect.length === 0) return null;
  try {
    const parsed = new URL(redirect, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

namespace Easyfy.Modules {
  @invoke
  class Form {
    constructor() {}

    onInit() {
      globalEvent({
        type: 'submit',
        event: {
          action: 'ajax-form',
          callback: Form.onPostForm,
        },
      });

      globalEvent({
        type: 'submit',
        event: {
          action: 'pre-post',
          callback: Form.onPrePostAction,
        },
      });
    }

    static onPostForm = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLFormElement = e.target as HTMLFormElement;
      const data: FormData = new FormData(element);
      const dataUpdate = 'data-update';
      const dataHiddenValue = 'data-hidden-value';
      const datamessageContainer = element.getAttribute('data-message');
      const messageContainer = element.querySelector(`[data-container="${datamessageContainer}"]`) as HTMLElement;

      const tokenEl: HTMLElement = element.closest('[data-token]') as HTMLElement;
      const token: string =
        tokenEl && tokenEl.dataset?.token
          ? tokenEl.dataset?.token
          : ((document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement)?.value ?? '');

      PostForm(element.getAttribute('action') ?? '', data, token).then((response) => {
        if (response.data.message) {
          if (messageContainer) {
            easyfyToaster.toaster(response.data.success ? MessageSeverity.success : MessageSeverity.error, response.data.message, messageContainer);
          } else {
            easyfyToaster.toaster(response.data.success ? MessageSeverity.success : MessageSeverity.error, response.data.message);
          }
        }

        if (response.data.success) {
          if (response.data.partial) {
            const updateContainer = element.getAttribute(dataUpdate);
            const container = document.querySelector(`[data-container="${updateContainer}"]`);
            if (container) {
              container.innerHTML = response.data.partial;

              // clear field for next form load
              const hiddenValue = element.querySelector<HTMLInputElement>(`[${dataHiddenValue}]`)?.value;
              if (hiddenValue) {
                element.setAttribute(dataUpdate, element.getAttribute(dataUpdate)?.replace(`-${hiddenValue}`, '') ?? '');
              }
            }

            const extraToggleEvent = element.getAttribute('data-extra-toggle-event');
            if (extraToggleEvent) {
              const container = document.querySelector(`[data-container="${extraToggleEvent}"]`);
              if (container) hide(container);

              const overlayElement: NodeListOf<HTMLElement> = document.querySelectorAll('[data-overlay-container]');
              hideAll(overlayElement);
            }
          } else if (response.data.redirect) {
            const safeRedirect = toSameOriginPath(response.data.redirect);
            if (safeRedirect) {
              setTimeout(function () {
                location.href = safeRedirect;
              }, 700);
            }
          } else if (response.data.reload) {
            setTimeout(function () {
              location.reload();
            }, 700);
          }
        }
      });

      return false;
    };

    static onPrePostAction = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const form: HTMLFormElement = e.target as HTMLFormElement;
      const data: FormData = new FormData(form);
      const datamessageContainer = form.getAttribute('data-message');
      const messageContainer = form.querySelector(`[data-container="${datamessageContainer}"]`) as HTMLElement;
      const validationMethod = form.getAttribute('data-validate-method');

      const tokenEl: HTMLElement = form.closest('[data-token]') as HTMLElement;
      const token: string =
        tokenEl && tokenEl.dataset?.token
          ? tokenEl.dataset?.token
          : ((document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement)?.value ?? '');

      if (!validationMethod) return;

      PostForm(validationMethod, data, token).then((response) => {
        if (response.data.success) {
          form.submit();
        } else {
          if (messageContainer) {
            easyfyToaster.toaster(response.data.success ? MessageSeverity.success : MessageSeverity.error, response.data.message, messageContainer);
          } else {
            easyfyToaster.toaster(response.data.success ? MessageSeverity.success : MessageSeverity.error, response.data.message);
          }
        }
      });

      return false;
    };
  }
}
